import React, { FocusEvent, useCallback } from 'react';
import _ from 'lodash';
import { useDispatch } from 'react-redux';
import { Formik, Field } from 'formik';
import { Modal as BootstrapModal, Button } from 'react-bootstrap';
import {
  FormGroup,
  TextboxField,
  Modal,
  SelectDropdownField,
  validateTrustedContactFields,
} from '@tradingblock/components';
import { useStateSelector, apiTokenSelector } from '../../../data/global/dataSelectors';
import { AccountManagementDataActions } from '../../../data/global/actions/AccountManagementActions';
import {
  AllStates,
  Country,
  DefaultPhoneValues,
  DefaultTrustedContactDetails,
  ErrorType,
  getGroupedCountryOptions,
  TrustedContactModel,
  TrustedContactPhoneModel,
  TrustedContactUpdateFormValuesWrapper,
} from '@tradingblock/types';
import { useClearer } from '../../../hooks/useClearer';
import Config from '../../../config';

export const TrustedContactUpdate: React.FunctionComponent<{
  show?: boolean;
  toggleModal: () => void;
}> = ({ show, toggleModal }) => {
  const accountId = useStateSelector(s => s.accounts.account && s.accounts.account.AccountId);
  const clearer = useClearer();
  const dispatch = useDispatch();
  const apiToken = useStateSelector(apiTokenSelector);
  const { firstName, lastName, address, phones, email } = useStateSelector(
    s => s.accountManagement.accountManagementDetails.details.trustedContact || DefaultTrustedContactDetails
  );
  const initialTrustedContactValues: TrustedContactUpdateFormValuesWrapper = {
    trustedContact: {
      firstName: firstName ? firstName : '',
      lastName: lastName ? lastName : '',
      address: {
        address1: address !== null && address.address1 ? address.address1 : '',
        address2: address !== null && address.address2 ? address.address2 : '',
        city: address !== null && address.city ? address.city : '',
        state: address !== null && address.state ? address.state : '',
        postalCode: address !== null && address.postalCode ? address.postalCode : '',
        country: address !== null && address.country ? address.country : '',
      },
      phones: (phones ? [...phones] : [DefaultPhoneValues]) as TrustedContactPhoneModel[],
      email: email ? email : '',
    },
  };

  const onValidate = (values: TrustedContactUpdateFormValuesWrapper) => {
    const validationErrors: { [key: string]: ErrorType | undefined | string } = validateTrustedContactFields(
      values,
      Config.tradingApi,
      apiToken
    );

    Object.keys(validationErrors).forEach(
      validationErrorKey => !validationErrors[validationErrorKey] && delete validationErrors[validationErrorKey]
    );

    return validationErrors;
  };

  const onSubmit = useCallback(
    (values: TrustedContactUpdateFormValuesWrapper, { resetForm }) => {
      let updateValues: { item: string; trustedContact: TrustedContactModel } | {} = {};

      updateValues = {
        item: 'TrustedContact',
        trustedContact: values.trustedContact,
      };
      dispatch(
        AccountManagementDataActions.requestMakeAccountUpdate({
          accountId: accountId,
          request: updateValues,
        })
      );
      resetForm();
      toggleModal();

      sessionStorage.setItem('trustedContactUspsResponse', 'undefined');
    },
    [toggleModal]
  );

  return (
    <Formik
      initialValues={initialTrustedContactValues}
      onSubmit={onSubmit}
      validateOnChange={true}
      validate={onValidate}
    >
      {({ handleSubmit, resetForm, values, setFieldValue }) => (
        <Modal setShow={() => toggleModal()} show={show}>
          <BootstrapModal.Header closeButton>
            <BootstrapModal.Title>Trusted Contact Update</BootstrapModal.Title>
          </BootstrapModal.Header>
          <BootstrapModal.Body>
            <form id="addressUpdate" onSubmit={handleSubmit}>
              <h2 className="fields-title">Trusted Contact Personal Information</h2>
              <FormGroup>
                <Field component={TextboxField} id="trustedContact.firstName" label="First name" />
              </FormGroup>
              <FormGroup>
                <Field component={TextboxField} id="trustedContact.lastName" label="Last name" />
              </FormGroup>
              <FormGroup>
                <Field component={TextboxField} id="trustedContact.email" label="Email" />
              </FormGroup>
              <FormGroup>
                <Field component={TextboxField} id="trustedContact.phones[0].phoneNumber" label="Phone number" />
              </FormGroup>
              <h2 className="fields-title">Trusted Contact Address</h2>
              <>
                <FormGroup>
                  <Field
                    component={SelectDropdownField}
                    id="trustedContact.address.country"
                    label="Country"
                    options={getGroupedCountryOptions(clearer)}
                    optionsDefaultLabelId="country"
                  />
                </FormGroup>
                <FormGroup>
                  <Field component={TextboxField} id="trustedContact.address.address1" label="Address 1" />
                </FormGroup>
                <FormGroup>
                  <Field component={TextboxField} id="trustedContact.address.address2" label="Address 2" />
                </FormGroup>
                <FormGroup>
                  <Field component={TextboxField} id="trustedContact.address.city" label="City" />
                </FormGroup>
                <FormGroup>
                  {values.trustedContact.address.country === Country.UnitedStatesOfAmerica && (
                    <Field
                      component={SelectDropdownField}
                      id="trustedContact.address.state"
                      label="State"
                      options={AllStates}
                      optionsDefaultLabelId="state"
                    />
                  )}
                  {values.trustedContact.address.country !== Country.UnitedStatesOfAmerica &&
                    values.trustedContact.address.country !== '' && (
                      <Field component={TextboxField} id="trustedContact.address.state" label="Province" />
                    )}
                </FormGroup>
                <FormGroup>
                  <Field component={TextboxField} id="trustedContact.address.postalCode" label="Postal Code" />
                </FormGroup>
              </>
            </form>
          </BootstrapModal.Body>
          <BootstrapModal.Footer className="modal-footer-justified">
            <Button
              variant="secondary"
              onClick={() => {
                resetForm();
                toggleModal();
                sessionStorage.setItem('trustedContactUspsResponse', 'undefined');
              }}
            >
              Cancel
            </Button>
            <Button variant="primary" onClick={(values: any) => handleSubmit(values)}>
              Submit
            </Button>
          </BootstrapModal.Footer>
        </Modal>
      )}
    </Formik>
  );
};
